package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveCombat
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.usecase.GetActiveCombatUseCase
import fr.manyverse.app.domain.usecase.CombatNextTurnUseCase
import fr.manyverse.app.domain.usecase.ObserveActiveCombatUseCase
import fr.manyverse.app.domain.usecase.StartCombatUseCase
import fr.manyverse.app.domain.usecase.StopCombatUseCase
import fr.manyverse.app.domain.usecase.UpdateCombatParticipantsUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * État UI du module Combat.
 *
 * - [isPrepareOpen] : écran de préparation du combat ouvert (MJ uniquement).
 * - [isExpanded]    : vue détaillée ouverte (overlay MJ « C'est son tour » / frise joueur).
 * - [isMinimized]   : bulle flottante affichée.
 *
 * [isExpanded] et [isMinimized] sont mutuellement exclusifs (réduire/restaurer bascule entre les deux).
 */
data class CombatUiState(
    val combat: ActiveCombat? = null,
    val isPrepareOpen: Boolean = false,
    val isExpanded: Boolean = false,
    val isMinimized: Boolean = false
)

/**
 * ViewModel autonome du module Combat (instancié côté MJ et côté joueur),
 * calqué sur [WhiteboardViewModel].
 */
class CombatViewModel(
    private val startCombatUseCase: StartCombatUseCase,
    private val combatNextTurnUseCase: CombatNextTurnUseCase,
    private val updateCombatParticipantsUseCase: UpdateCombatParticipantsUseCase,
    private val stopCombatUseCase: StopCombatUseCase,
    private val observeActiveCombatUseCase: ObserveActiveCombatUseCase,
    private val getActiveCombatUseCase: GetActiveCombatUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _launchState = MutableStateFlow<AsyncUiState<ActiveCombat>>(AsyncUiState.Idle)
    val launchState: StateFlow<AsyncUiState<ActiveCombat>> = _launchState.asStateFlow()

    private val _state = MutableStateFlow(CombatUiState())
    val state: StateFlow<CombatUiState> = _state.asStateFlow()

    private var observeJob: Job? = null
    private var observingTableId: String? = null
    private var observingUserId: String? = null

    // --- GM: préparation -----------------------------------------------------

    fun openPrepare() {
        _state.value = _state.value.copy(isPrepareOpen = true)
    }

    fun closePrepare() {
        _state.value = _state.value.copy(isPrepareOpen = false)
    }

    fun resetLaunchState() {
        _launchState.value = AsyncUiState.Idle
    }

    /** Lance le combat (MJ). Ouvre l'overlay détaillé en cas de succès. */
    fun startCombat(
        tableId: String,
        createdBy: String,
        participants: List<CombatParticipant>
    ) {
        launchAsyncWithCallback(
            state = _launchState,
            block = { startCombatUseCase(tableId, createdBy, participants) },
            onSuccess = { combat ->
                _state.value = CombatUiState(
                    combat = combat,
                    isPrepareOpen = false,
                    isExpanded = true,
                    isMinimized = false
                )
            }
        )
    }

    // --- Bulle / overlay -----------------------------------------------------

    fun minimize() {
        _state.value = _state.value.copy(isExpanded = false, isMinimized = true)
    }

    fun restore() {
        _state.value = _state.value.copy(isExpanded = true, isMinimized = false)
    }

    // --- GM: déroulement du combat ------------------------------------------

    /** Passe au participant suivant (MJ). */
    fun nextTurn() {
        val combat = _state.value.combat ?: return
        if (combat.participants.isEmpty()) return
        val nextIndex = (combat.activeIndex + 1) % combat.participants.size
        _state.value = _state.value.copy(combat = combat.copy(activeIndex = nextIndex))
        viewModelScope.launch {
            combatNextTurnUseCase(combat).onFailure { revertTo(combat, it) }
        }
    }

    /** Réordonne un participant (MJ). */
    fun moveParticipant(fromIndex: Int, toIndex: Int) {
        val combat = _state.value.combat ?: return
        val participants = combat.participants
        if (fromIndex !in participants.indices || toIndex !in participants.indices) return
        if (fromIndex == toIndex) return
        val reordered = participants.toMutableList().apply {
            add(toIndex, removeAt(fromIndex))
        }
        applyParticipantsChange(combat, reordered)
    }

    /** Retire un participant du combat (MJ). */
    fun removeParticipant(participantId: String) {
        val combat = _state.value.combat ?: return
        val remaining = combat.participants.filterNot { it.id == participantId }
        applyParticipantsChange(combat, remaining)
    }

    private fun applyParticipantsChange(
        combat: ActiveCombat,
        newParticipants: List<CombatParticipant>
    ) {
        if (newParticipants.isEmpty()) {
            stopCombat()
            return
        }
        // Recalcule l'index actif localement pour suivre le même participant.
        val activeId = combat.participants.getOrNull(combat.activeIndex)?.id
        val keptIndex = newParticipants.indexOfFirst { it.id == activeId }
        val newActiveIndex = if (keptIndex >= 0) keptIndex
        else combat.activeIndex.coerceIn(0, newParticipants.lastIndex)

        _state.value = _state.value.copy(
            combat = combat.copy(participants = newParticipants, activeIndex = newActiveIndex)
        )
        viewModelScope.launch {
            updateCombatParticipantsUseCase(combat, newParticipants).onFailure { revertTo(combat, it) }
        }
    }

    /**
     * Annule une modification affichée avant son envoi, qui a échoué : sans cela, le MJ verrait
     * un tour ou un ordre que les joueurs n'ont jamais reçu.
     */
    private fun revertTo(combat: ActiveCombat, error: Throwable) {
        if (_state.value.combat?.id == combat.id) {
            _state.value = _state.value.copy(combat = combat)
        }
        errorNotifier.notify(error)
    }

    /** Arrête le combat pour tout le monde (MJ). */
    fun stopCombat() {
        val combat = _state.value.combat ?: return
        viewModelScope.launch {
            stopCombatUseCase(combat.id)
                .onSuccess { resetFullState() }
                .onFailure(errorNotifier::notify)
        }
    }

    // --- Observation temps réel (MJ + joueur) -------------------------------

    /**
     * Observe le combat actif. À l'apparition d'un combat (snapshot de reconnexion
     * ou diffusion d'un nouveau combat), l'affiche en bulle minimisée ;
     * son arrêt (null) réinitialise l'état.
     */
    fun startObservingActiveCombat(tableId: String, currentUserId: String) {
        if (observeJob?.isActive == true && observingTableId == tableId && observingUserId == currentUserId) {
            return
        }
        observeJob?.cancel()
        observingTableId = tableId
        observingUserId = currentUserId
        observeJob = viewModelScope.launch {
            getActiveCombatUseCase(tableId).onSuccess { existing ->
                if (existing != null && _state.value.combat == null) {
                    showAsBubble(existing)
                }
            }
            observeActiveCombatUseCase(tableId, currentUserId).collect { combat ->
                if (combat == null) {
                    resetFullState()
                } else {
                    val isNew = _state.value.combat?.id != combat.id
                    if (isNew) {
                        showAsBubble(combat)
                    } else {
                        // Même combat mis à jour (tour, ordre) : conserver l'affichage courant.
                        _state.value = _state.value.copy(combat = combat)
                    }
                }
            }
        }
    }

    fun refreshOnForeground(tableId: String, currentUserId: String) {
        viewModelScope.launch {
            getActiveCombatUseCase(tableId).onSuccess { combat ->
                if (combat == null) {
                    if (_state.value.combat != null) resetFullState()
                    return@onSuccess
                }
                if (_state.value.combat?.id == combat.id) {
                    _state.value = _state.value.copy(combat = combat)
                } else {
                    showAsBubble(combat)
                }
            }
        }
    }

    private fun showAsBubble(combat: ActiveCombat) {
        _state.value = _state.value.copy(
            combat = combat,
            isExpanded = false,
            isMinimized = true
        )
    }

    private fun resetFullState() {
        _state.value = CombatUiState(isPrepareOpen = _state.value.isPrepareOpen)
    }

    override fun onCleared() {
        super.onCleared()
        observeJob?.cancel()
    }
}
